import React, { useState, useEffect } from 'react';

function BurnSnifferDashboard({ jwtToken }) {
  const [snifferState, setSnifferState] = useState(null);
  const [recentBurns, setRecentBurns] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [debugResults, setDebugResults] = useState(null);

  const fetchSnifferData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [stateRes, burnsRes, healthRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=burn-sniffer-state`, {
          headers: { 'Authorization': `Bearer ${jwtToken}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=recent-burns&limit=20`, {
          headers: { 'Authorization': `Bearer ${jwtToken}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=burn-sniffer-health`, {
          headers: { 'Authorization': `Bearer ${jwtToken}` }
        })
      ]);

      if (stateRes.ok) {
        const stateData = await stateRes.json();
        setSnifferState(stateData);
      }

      if (burnsRes.ok) {
        const burnsData = await burnsRes.json();
        setRecentBurns(burnsData.burns || []);
        setStats(burnsData.stats || {});
      }

      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setSystemHealth(healthData);
      }
    } catch (error) {
      console.error('Error fetching sniffer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerManualScan = async (scanType = 'quick') => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=trigger-burn-scan&scanType=${scanType}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      
      const result = await response.json();
      if (result.success) {
        if (scanType === 'quick') {
          alert(`Quick scan completed successfully!\n\nProcessed ${result.blocksScanned} blocks\nFound ${result.result?.data?.burnsFound || 0} burns`);
        } else {
          alert(`Full scan started successfully!\n\nProcessing ${result.blocksToScan} blocks in background\nRefresh the dashboard in a few minutes to see results`);
        }
        await fetchSnifferData(); // Refresh data
      } else {
        alert('Manual scan failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error triggering manual scan:', error);
      alert('Failed to trigger manual scan: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const debugSpecificTransaction = async () => {
    const txHash = '0x40bf44790e891dbb10a767679e60e259b0376948c8f432ddf4cc268376cfd804';
    setLoading(true);
    
    try {
      // First check block range
      const blockResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=check-transaction-block&txHash=${txHash}`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      
      const blockResult = await blockResponse.json();
      
      // Then debug the burn details
      const burnResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=debug-specific-burn&txHash=${txHash}`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      
      const burnResult = await burnResponse.json();
      
      setDebugResults({
        blockCheck: blockResult,
        burnCheck: burnResult,
        timestamp: new Date().toLocaleString()
      });
      
    } catch (error) {
      console.error('Error debugging transaction:', error);
      alert('Failed to debug transaction: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnifferData();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchSnifferData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#28a745';
      case 'warning': 
      case 'degraded': return '#ffc107';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'websocket': return '⚡';
      case 'backup_cron': return '🕐';
      case 'cron': return '⏰';
      default: return '🔄';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const isWebSocketActive = () => {
    if (!snifferState?.websocket_status) return false;
    
    const lastActivity = new Date(snifferState.websocket_status.created_at);
    const now = new Date();
    const diffMinutes = (now - lastActivity) / (1000 * 60);
    
    return diffMinutes < 5; // Consider active if activity within 5 minutes
  };

  const getWebSocketStatus = () => {
    if (!snifferState?.websocket_status) return { status: 'unknown', message: 'No WebSocket activity detected' };
    
    const wsStatus = snifferState.websocket_status;
    const isActive = isWebSocketActive();
    
    if (wsStatus.event_type === 'initialized' && isActive) {
      return { status: 'healthy', message: 'WebSocket connected and monitoring' };
    } else if (wsStatus.event_type.includes('error')) {
      return { status: 'error', message: wsStatus.message };
    } else if (!isActive) {
      return { status: 'warning', message: 'WebSocket may be disconnected (no recent activity)' };
    } else {
      return { status: 'healthy', message: wsStatus.message };
    }
  };

  return (
    <div style={{ padding: '20px', color: 'white' }}>
      {/* Header with status and controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ color: '#FFD700', margin: '0 0 0.5rem 0' }}>🔥 Burn Sniffer System</h2>
          {systemHealth && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ 
                color: getStatusColor(systemHealth.overall_status),
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
                ● {systemHealth.overall_status.toUpperCase()}
              </span>
              {systemHealth.overall_status === 'healthy' && <span>All systems operational</span>}
              {systemHealth.overall_status === 'degraded' && <span>Minor issues detected</span>}
              {systemHealth.overall_status === 'warning' && <span>Some systems need attention</span>}
              {systemHealth.overall_status === 'error' && <span>Critical issues detected</span>}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          
          <button 
            onClick={fetchSnifferData}
            disabled={loading}
            className="rainbowBtn"
            style={{ margin: 0 }}
          >
            {loading ? '⏳' : '🔄'} Refresh
          </button>
          
          <button 
            onClick={() => triggerManualScan('quick')}
            disabled={loading}
            className="rainbowBtn"
            style={{ margin: 0, backgroundColor: '#28a745' }}
          >
            {loading ? '⏳ Quick Scan' : '⚡ Quick Scan'}
          </button>
          
          <button 
            onClick={() => triggerManualScan('full')}
            disabled={loading}
            className="rainbowBtn"
            style={{ margin: 0, backgroundColor: '#28a745' }}
          >
            {loading ? '⏳ Full Scan' : '⚡ Full Scan'}
          </button>
          
          <button 
            onClick={debugSpecificTransaction}
            disabled={loading}
            className="rainbowBtn"
            style={{ margin: 0, backgroundColor: '#dc3545' }}
          >
            {loading ? '⏳ Debug TX' : '🔍 Debug Missing TX'}
          </button>
        </div>
      </div>

      {/* System Status Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        {/* WebSocket Status */}
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: '#FFD700', margin: '0 0 15px 0' }}>⚡ WebSocket Status</h4>
          {(() => {
            const wsStatus = getWebSocketStatus();
            return (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  marginBottom: '10px'
                }}>
                  <span style={{ 
                    color: getStatusColor(wsStatus.status),
                    fontSize: '1.5rem'
                  }}>●</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {wsStatus.status.toUpperCase()}
                  </span>
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  {wsStatus.message}
                </div>
                {snifferState?.websocket_status && (
                  <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '10px' }}>
                    Last activity: {formatTimestamp(snifferState.websocket_status.created_at)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Processing Stats */}
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: '#FFD700', margin: '0 0 15px 0' }}>📊 24h Processing Stats</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Total Burns:</span>
              <span style={{ fontWeight: 'bold' }}>{stats.total_24h || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>⚡ WebSocket:</span>
              <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                {stats.by_source?.websocket || 0} ({stats.websocket_percentage || 0}%)
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>🕐 Backup Cron:</span>
              <span style={{ fontWeight: 'bold' }}>{stats.by_source?.backup_cron || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>✅ Success:</span>
              <span style={{ fontWeight: 'bold', color: '#28a745' }}>{stats.by_status?.success || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>❌ Failed:</span>
              <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{stats.by_status?.failed || 0}</span>
            </div>
          </div>
        </div>

        {/* System Health */}
        {systemHealth?.health && (
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '20px', 
            borderRadius: '15px'
          }}>
            <h4 style={{ color: '#FFD700', margin: '0 0 15px 0' }}>🏥 System Health</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {Object.entries(systemHealth.health).slice(0, 5).map(([metric, data]) => (
                <div key={metric} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9rem' }}>
                    {metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                  </span>
                  <span style={{ 
                    color: getStatusColor(data.status),
                    fontWeight: 'bold',
                    fontSize: '0.8rem'
                  }}>
                    {data.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Burns Table */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '15px', 
        overflow: 'hidden',
        marginBottom: '20px'
      }}>
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h4 style={{ color: 'white', margin: 0 }}>🔥 Recent Burns (Last 24h)</h4>
          <span style={{ opacity: 0.7 }}>{recentBurns.length} burns</span>
        </div>
        
        {recentBurns.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            No recent burns detected
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Source</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Time</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Collection</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Token ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Burner</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'white' }}>Mint TX</th>
                </tr>
              </thead>
              <tbody>
                {recentBurns.map((burn, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <td style={{ padding: '12px' }}>
                      <span style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px',
                        color: burn.source === 'websocket' ? '#28a745' : '#ffc107'
                      }}>
                        {getSourceIcon(burn.source)} {burn.source}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'white', fontSize: '0.8rem' }}>
                      {formatTimestamp(burn.processed_at)}
                    </td>
                    <td style={{ padding: '12px', color: 'white', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {burn.collection.slice(0, 8)}...
                    </td>
                    <td style={{ padding: '12px', color: 'white' }}>
                      {burn.token_id}
                    </td>
                    <td style={{ padding: '12px', color: 'white', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {burn.burner_address.slice(0, 6)}...{burn.burner_address.slice(-4)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: 'white',
                        background: burn.overall_status === 'completed' ? '#28a745' : 
                                   burn.overall_status === 'failed' ? '#dc3545' : '#ffc107'
                      }}>
                        {burn.overall_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {burn.mint_tx_hash ? (
                        <a 
                          href={`https://polygonscan.com/tx/${burn.mint_tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#FFD700', textDecoration: 'none', fontSize: '0.8rem' }}
                        >
                          {burn.mint_tx_hash.slice(0, 8)}...
                        </a>
                      ) : (
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Debug Results */}
      {debugResults && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px', 
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <h4 style={{ color: 'white', margin: 0 }}>🔍 Debug Results</h4>
            <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>{debugResults.timestamp}</span>
          </div>
          
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: '1fr 1fr' }}>
            {/* Block Range Check */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: '15px', 
              borderRadius: '10px' 
            }}>
              <h5 style={{ color: '#FFD700', margin: '0 0 10px 0' }}>📊 Block Range Analysis</h5>
              {debugResults.blockCheck?.success ? (
                <div style={{ fontSize: '0.9rem', color: 'white' }}>
                  <div>Transaction Block: <strong>{debugResults.blockCheck.transactionBlock}</strong></div>
                  <div>Last Processed: <strong>{debugResults.blockCheck.lastProcessedBlock}</strong></div>
                  <div>Current Block: <strong>{debugResults.blockCheck.currentBlock}</strong></div>
                  <div style={{ 
                    color: debugResults.blockCheck.isInRange ? '#28a745' : '#dc3545',
                    fontWeight: 'bold',
                    marginTop: '10px'
                  }}>
                    {debugResults.blockCheck.isInRange ? '✅ In Scan Range' : '❌ Not in Scan Range'}
                  </div>
                  {debugResults.blockCheck.analysis.alreadyScanned && (
                    <div style={{ color: '#ffc107', marginTop: '5px' }}>
                      ⚠️ Already scanned - block is older than last processed
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#dc3545' }}>
                  Error: {debugResults.blockCheck?.error || 'Unknown error'}
                </div>
              )}
            </div>

            {/* Burn Details Check */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: '15px', 
              borderRadius: '10px' 
            }}>
              <h5 style={{ color: '#FFD700', margin: '0 0 10px 0' }}>🔥 Burn Analysis</h5>
              {debugResults.burnCheck?.success ? (
                <div style={{ fontSize: '0.9rem', color: 'white' }}>
                  <div>Transfer Events: <strong>{debugResults.burnCheck.debugInfo?.transferEvents?.length || 0}</strong></div>
                  <div>Matching Burns: <strong>{debugResults.burnCheck.matchingBurns?.length || 0}</strong></div>
                  
                  {debugResults.burnCheck.analysis && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ color: debugResults.burnCheck.analysis.hasTransferEvents ? '#28a745' : '#dc3545' }}>
                        {debugResults.burnCheck.analysis.hasTransferEvents ? '✅' : '❌'} Has Transfer Events
                      </div>
                      <div style={{ color: debugResults.burnCheck.analysis.hasMatchingContract ? '#28a745' : '#dc3545' }}>
                        {debugResults.burnCheck.analysis.hasMatchingContract ? '✅' : '❌'} From Monitored Collection
                      </div>
                      <div style={{ color: debugResults.burnCheck.analysis.hasBurnAddress ? '#28a745' : '#dc3545' }}>
                        {debugResults.burnCheck.analysis.hasBurnAddress ? '✅' : '❌'} To Burn Address
                      </div>
                      {debugResults.burnCheck.analysis.collectionType && debugResults.burnCheck.analysis.collectionType !== 'none' && (
                        <div style={{ color: '#FFD700', fontStyle: 'italic', fontSize: '0.8rem' }}>
                          Collection Type: {debugResults.burnCheck.analysis.collectionType === 'approved' ? 'Approved (Single Upgrade)' : 'Recipe Collection'}
                        </div>
                      )}
                      <div style={{ 
                        color: debugResults.burnCheck.analysis.shouldBeDetected ? '#28a745' : '#dc3545',
                        fontWeight: 'bold',
                        marginTop: '5px'
                      }}>
                        {debugResults.burnCheck.analysis.shouldBeDetected ? '🎯 Should Be Detected' : '🚫 Should NOT Be Detected'}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#dc3545' }}>
                  Error: {debugResults.burnCheck?.error || 'Unknown error'}
                </div>
              )}
            </div>
          </div>
          
          {/* Detailed Transfer Events */}
          {debugResults.burnCheck?.success && debugResults.burnCheck.debugInfo?.transferEvents?.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <h5 style={{ color: '#FFD700', margin: '0 0 10px 0' }}>📋 Transfer Events Details</h5>
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.3)', 
                padding: '10px', 
                borderRadius: '5px',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <pre style={{ margin: 0, color: 'white' }}>
                  {JSON.stringify(debugResults.burnCheck.debugInfo.transferEvents, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <button 
              onClick={() => setDebugResults(null)}
              className="rainbowBtn"
              style={{ margin: 0, backgroundColor: '#6c757d' }}
            >
              Clear Debug Results
            </button>
          </div>
        </div>
      )}

      {/* WebSocket Logs */}
      {systemHealth?.websocket_logs && systemHealth.websocket_logs.length > 0 && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px', 
          padding: '20px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 15px 0' }}>⚡ Recent WebSocket Activity</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {systemHealth.websocket_logs.map((log, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}>
                <span>
                  <span style={{ 
                    color: log.event_type.includes('error') ? '#dc3545' : '#28a745',
                    fontWeight: 'bold'
                  }}>
                    {log.event_type}
                  </span>
                  : {log.message}
                </span>
                <span style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  {formatTimestamp(log.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div style={{ 
        background: 'rgba(0, 123, 255, 0.1)', 
        border: '1px solid rgba(0, 123, 255, 0.3)',
        borderRadius: '10px', 
        padding: '15px',
        marginTop: '20px'
      }}>
        <h5 style={{ color: '#007bff', margin: '0 0 10px 0' }}>💡 System Architecture</h5>
        <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
          <strong>Primary:</strong> WebSocket real-time burn detection (instant processing)<br/>
          <strong>Backup:</strong> 4-hour cron job safety net (catches any missed burns)<br/>
          <strong>Protection:</strong> Duplicate detection prevents double rewards<br/>
          <strong>Monitoring:</strong> This dashboard tracks all activity and system health
        </div>
      </div>
    </div>
  );
}

export default BurnSnifferDashboard; 